import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const backgroundImages = [
        '/padel_arena.png',
        '/padel_celebration.png',
        '/padel_highlight.png',
        '/padel_action.png'
    ];

    useEffect(() => {
        document.title = 'Cómo Funciona - VisioPadel';
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    const steps = [
        {
            number: '01',
            title: 'Crea tu Espacio',
            description: 'Configura tu club, da de alta tus pistas y personaliza tu perfil de organizador en minutos.',
            icon: '🏟️'
        },
        {
            number: '02',
            title: 'Publica Eventos',
            description: 'Crea torneos, pozos o ligas con un clic. Define niveles, precios y formatos de juego fácilmente.',
            icon: '📅'
        },
        {
            number: '03',
            title: 'Gestión Automática',
            description: 'Olvídate del excel. Los cuadros, horarios y resultados se generan y actualizan automáticamente.',
            icon: '⚙️'
        },
        {
            number: '04',
            title: 'Fideliza y Monetiza',
            description: 'Ofrece una experiencia profesional a tus jugadores con rankings en vivo y pagos integrados.',
            icon: '📈'
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-[#0f172a]/95 backdrop-blur-md h-20 md:h-24 transition-all border-b border-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="flex justify-between items-center h-full">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src="/assets/SVG/LogoVisioPadelBlanco.svg?v=2" 
                                alt="VisioPadel" 
                                className="h-14 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-8 text-[15px] font-light text-slate-200">
                             <div className="flex flex-col items-center opacity-60 cursor-not-allowed">
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-slate-400">
                                    Gestiona tu club
                                </div>
                                <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-slate-700 -mt-1">Próximamente</span>
                            </div>
                            <Link to="/pricing" className="nav-link-xporty hover:text-white transition-colors">Organiza tu evento</Link>
                            <Link to="/about" className="nav-link-xporty text-white font-medium transition-colors active">Cómo Funciona</Link>
                            <Link to="/login" className="nav-link-xporty hover:text-white transition-colors">Entrar</Link>
                            <Link to="/signup" className="nav-link-xporty hover:text-white transition-colors">Registrarse</Link>
                        </div>



                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-white"
                        >
                             <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative pt-24 pb-32 md:pt-36 md:pb-48 overflow-hidden">
                 <div className="absolute inset-0 z-0">
                     {backgroundImages.map((image, index) => (
                        <div
                            key={image}
                            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
                            style={{ backgroundImage: `url('${image}')` }}
                        ></div>
                    ))}
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-[2px]"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
                    <span className="inline-block py-1 px-3 rounded-full bg-white/10 text-white/90 backdrop-blur-md border border-white/20 text-sm font-semibold mb-4 tracking-wide uppercase">
                        Para Clubs y Organizadores
                    </span>
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight leading-none drop-shadow-lg">
                        Simplifica tu <span className="text-[var(--primary-brand)]">Gestión</span>
                    </h1>
                    <p className="text-xl text-slate-200 mb-10 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md">
                        Automatiza torneos, pozos y ligas. Maximiza la rentabilidad de tus pistas y ofrece la mejor experiencia.
                    </p>
                </div>
            </div>

            {/* Steps Section */}
            <div className="max-w-7xl mx-auto px-4 -mt-32 relative z-20 pb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {steps.map((step, index) => (
                        <div key={index} className="relative group p-8 bg-white rounded-[2.5rem] border border-slate-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                            {/* Decorative number background */}
                            <div className="absolute top-4 right-6 text-7xl font-black text-slate-50 group-hover:text-slate-100 transition-colors z-0 select-none">
                                {step.number}
                            </div>

                            <div className="relative z-10">
                                <div className="text-5xl mb-8 group-hover:scale-110 transition-transform inline-flex items-center justify-center bg-slate-50 w-20 h-20 rounded-2xl group-hover:bg-[var(--primary-brand)]/10 group-hover:text-green-600 shadow-inner">
                                    {step.icon}
                                </div>
                                <h3 className="text-2xl font-black text-slate-900 mb-4">{step.title}</h3>
                                <p className="text-slate-600 font-medium leading-relaxed">
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA */}
            <div className="py-24 bg-[#0f172a] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--primary-brand)]/20 rounded-full blur-[120px]"></div>
                 <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#0f172a] rounded-full blur-[100px] z-10"></div>
                 
                <div className="container mx-auto px-4 text-center relative z-20">
                    <span className="inline-block py-1 px-3 rounded-full bg-[var(--primary-brand)]/20 text-[var(--primary-brand)] border border-[var(--primary-brand)]/30 text-sm font-bold uppercase tracking-wide mb-6">
                        Únete a la comunidad
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                        Lleva tu club al siguiente nivel
                    </h2>
                    <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                        Empieza hoy mismo a digitalizar tu club.
                    </p>
                    <Link
                        to="/signup"
                        className="inline-block bg-[var(--primary-brand)] hover:bg-[#65a30d] text-white font-bold py-5 px-12 rounded-2xl text-lg shadow-xl shadow-[var(--primary-brand)]/30 transition-all transform hover:-translate-y-1 hover:scale-105"
                    >
                        Comienza a Organizar Gratis
                    </Link>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-12">
                <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
                    <img src="/visiopadel-logo.png" alt="VisioPadel" className="h-10 opacity-70 grayscale hover:grayscale-0 transition-all" />
                    <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">© 2026 VisioPadel</p>
                </div>
            </footer>
        </div>
    );
};

export default HowItWorks;
